/**
 * TrackingTimeline — vertikale Timeline der TrackingEvents (UI-05-Teil).
 *
 * Sortierung: events werden ASC nach eventAt erwartet (Backend liefert das so).
 * Neuester Event erhaelt Ring-Highlight.
 * Empty-State: "Noch keine Tracking-Events vom Carrier."
 */

interface TimelineEvent {
  id: number;
  internalStatus: string;
  carrierStatus: string | null;
  eventAt: string;
  eventDescription: string | null;
  eventLocation: string | null;
  carrier: string;
}

interface TrackingTimelineProps {
  events: TimelineEvent[];
}

const dotColor: Record<string, string> = {
  pre_transit: 'bg-gray-400',
  in_transit: 'bg-blue-500',
  out_for_delivery: 'bg-orange-500',
  delivered: 'bg-green-500',
  exception: 'bg-red-500',
  returned: 'bg-purple-500',
  unknown: 'bg-gray-300',
};

const statusLabel: Record<string, string> = {
  pre_transit: 'Label gedruckt',
  in_transit: 'Unterwegs',
  out_for_delivery: 'In Zustellung',
  delivered: 'Zugestellt',
  exception: 'Problem',
  returned: 'Retoure',
  unknown: 'Unbekannt',
};

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function TrackingTimeline({ events }: TrackingTimelineProps) {
  if (events.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic py-4">
        Noch keine Tracking-Events vom Carrier.
      </div>
    );
  }

  return (
    <ol className="relative border-l border-[#DAD9D9] ml-3 space-y-4">
      {events.map((ev, idx) => {
        const isLast = idx === events.length - 1;
        return (
          <li key={ev.id} className="ml-4">
            <span
              className={`absolute -left-1.5 mt-1 flex items-center justify-center w-3 h-3 rounded-full ${dotColor[ev.internalStatus] ?? 'bg-gray-300'} ${isLast ? 'ring-2 ring-[#E9FF57]' : ''}`}
              aria-hidden="true"
            />
            <div className="text-xs text-gray-500">{formatDateTime(ev.eventAt)}</div>
            <div className="text-sm text-[#1A1A1A] font-medium">
              {statusLabel[ev.internalStatus] ?? ev.internalStatus}
              {ev.carrierStatus && <span className="ml-1 text-xs text-gray-500 font-normal">({ev.carrierStatus})</span>}
            </div>
            {ev.eventDescription && (
              <div className="text-xs text-gray-600 mt-0.5">{ev.eventDescription}</div>
            )}
            {ev.eventLocation && (
              <div className="text-xs text-gray-500 mt-0.5">{ev.eventLocation}</div>
            )}
            <div className="text-[10px] text-gray-400 uppercase mt-0.5">{ev.carrier}</div>
          </li>
        );
      })}
    </ol>
  );
}
